import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Bell, Check, Search } from "lucide-react";

/**
 * Admin-side dialog for choosing which pool-side users (workers, admins,
 * drivers, secretaries) receive email notifications for every ticket
 * event on a specific colony. Complements the always-included office
 * inbox — pick individual staff on top of that.
 */
export default function ColonyWatchersDialog({ colony, onOpenChange }) {
  const open = !!colony;
  const [eligible, setEligible] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    if (!colony) return;
    setLoading(true);
    try {
      const [eRes, wRes] = await Promise.all([
        api.get("/mgmt/eligible-watchers"),
        api.get(`/mgmt/colonies/${colony.id}/watchers`),
      ]);
      setEligible(eRes.data || []);
      setSelected(new Set((wRes.data?.watchers || []).map((w) => w.id)));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [colony]);

  useEffect(() => { if (open) { setQ(""); load(); } }, [open, load]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return eligible;
    return eligible.filter((u) =>
      (u.name || "").toLowerCase().includes(query) ||
      (u.email || "").toLowerCase().includes(query) ||
      (u.role || "").toLowerCase().includes(query)
    );
  }, [eligible, q]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/mgmt/colonies/${colony.id}/watchers`, {
        user_ids: Array.from(selected),
      });
      toast.success(`${selected.size} watcher(s) saved for ${colony.name}.`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="max-w-lg" data-testid="colony-watchers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-4 h-4" /> Ticket watchers — {colony?.name}
          </DialogTitle>
          <DialogDescription>
            Anyone selected here gets emailed on every new ticket, reply, and status change for this colony. The Management USA NJ inbox is always included on top of your choices.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
          <Input
            placeholder="Filter by name, email, or role"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9 rounded-sm"
            data-testid="colony-watcher-search"
          />
        </div>

        <div className="border border-border rounded-sm max-h-80 overflow-y-auto">
          {loading && <div className="px-3 py-6 text-center text-sm text-muted-foreground">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="colony-watchers-empty">
              {eligible.length === 0
                ? "No eligible users. Create worker logins from the Payroll → Manage workers dialog first."
                : "No users match your search."}
            </div>
          )}
          {filtered.map((u) => {
            const on = selected.has(u.id);
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => toggle(u.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 border-b border-border last:border-b-0 text-left transition ${
                  on ? "bg-emerald-50" : "hover:bg-secondary/40"
                }`}
                data-testid={`colony-watcher-toggle-${u.id}`}
              >
                <div className={`w-5 h-5 rounded-sm border ${on ? "bg-emerald-700 border-emerald-700 text-white" : "border-border"} flex items-center justify-center`}>
                  {on && <Check className="w-3.5 h-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {u.name || u.email}
                    <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground">
                      {u.role || "user"}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="text-xs text-muted-foreground">
          {selected.size} selected · Management USA NJ office inbox is always included
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="colony-watchers-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="colony-watchers-save">
            {saving ? "Saving…" : "Save watchers"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
